import React, { useState } from 'react';
import { MapPin, Clock, AlertTriangle, BarChart2, Zap } from 'lucide-react';

interface Resident {
  name: string;
  age: number;
  address: string;
  emergencyContact: string;
  careLevel: string;
  primaryConditions: string[];
}

interface ResidentMonitorProps {
  resident: Resident;
}

// Mock floor layout data - in a real app this would come from a database or API
const roomData = [
  { id: 'living', name: 'Living Room', x: 10, y: 30, width: 40, height: 30 },
  { id: 'kitchen', name: 'Kitchen', x: 50, y: 30, width: 30, height: 30 },
  { id: 'bedroom', name: 'Bedroom', x: 10, y: 10, width: 30, height: 20 },
  { id: 'bathroom', name: 'Bathroom', x: 40, y: 10, width: 20, height: 20 },
  { id: 'hallway', name: 'Hallway', x: 60, y: 10, width: 20, height: 20 },
  { id: 'entry', name: 'Entryway', x: 80, y: 30, width: 10, height: 30 }
];

// Simulated floor sensor data
const heatMapData = [
  { roomId: 'living', intensity: 0.8 },
  { roomId: 'kitchen', intensity: 0.3 },
  { roomId: 'bedroom', intensity: 0.1 },
  { roomId: 'bathroom', intensity: 0.4 },
  { roomId: 'hallway', intensity: 0.6 },
  { roomId: 'entry', intensity: 0.1 }
];

// Simulated path data representing resident movement
const pathData = [
  { x: 25, y: 20 }, // bedroom
  { x: 45, y: 20 }, // bathroom
  { x: 50, y: 25 }, // hallway
  { x: 40, y: 40 }, // living room
  { x: 60, y: 45 }, // kitchen
  { x: 30, y: 40 }  // back to living room (current position)
];

const ResidentMonitor: React.FC<ResidentMonitorProps> = ({ resident }) => {
  const [timeRange, setTimeRange] = useState('1h');
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showPath, setShowPath] = useState(true);
  
  // Current location - would be determined by real-time floor sensors
  const currentRoom = 'Living Room';
  const currentLocation = { x: 30, y: 40 };
  const lastMovementTime = new Date(Date.now() - 3 * 60 * 1000); // 3 minutes ago
  
  // Mock activity data
  const activityData = [
    { time: '8:00 AM', activity: 'Woke up, movement detected in Bedroom' },
    { time: '8:15 AM', activity: 'Bathroom visit (duration: 5 minutes)' },
    { time: '8:30 AM', activity: 'In Kitchen, likely preparing breakfast' },
    { time: '9:15 AM', activity: 'Movement to Living Room, seated position' },
    { time: '10:30 AM', activity: 'Increased movement in Living Room' },
    { time: '11:00 AM', activity: 'Brief visit to Kitchen' },
    { time: '11:30 AM', activity: 'Return to Living Room, minimal movement' }
  ];
  
  // Areas with abnormal patterns
  const abnormalPatterns = [
    {
      location: 'Bathroom',
      concern: 'Unsteady gait detected',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
      priority: 'MEDIUM'
    }
  ];

  // Generate colors for heatmap intensity
  const getHeatmapColor = (intensity: number) => {
    if (intensity > 0.7) return '#ef4444'; // red for high activity
    if (intensity > 0.4) return '#f59e0b'; // yellow/orange for medium
    return '#22c55e'; // green for low
  };

  const getOpacity = (intensity: number) => {
    return 0.1 + intensity * 0.5; // Scale opacity based on intensity
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold">Real-time Monitoring</h3>
          <p className="text-gray-600">Last updated: {new Date().toLocaleTimeString()}</p>
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={() => setTimeRange('1h')}
            className={`px-3 py-1 rounded-full text-sm ${
              timeRange === '1h' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            1h
          </button>
          <button 
            onClick={() => setTimeRange('4h')}
            className={`px-3 py-1 rounded-full text-sm ${
              timeRange === '4h' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            4h
          </button>
          <button 
            onClick={() => setTimeRange('12h')}
            className={`px-3 py-1 rounded-full text-sm ${
              timeRange === '12h' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            12h
          </button>
          <button 
            onClick={() => setTimeRange('24h')}
            className={`px-3 py-1 rounded-full text-sm ${
              timeRange === '24h' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            24h
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Floor map visualization */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden">
          <div className="border-b p-4 flex justify-between items-center">
            <h3 className="font-medium">Floor Activity Map</h3>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2 text-sm">
                <input 
                  type="checkbox" 
                  checked={showHeatmap} 
                  onChange={() => setShowHeatmap(!showHeatmap)}
                  className="rounded text-blue-500 focus:ring-blue-500"
                />
                <span>Heatmap</span>
              </label>
              <label className="flex items-center space-x-2 text-sm">
                <input 
                  type="checkbox" 
                  checked={showPath} 
                  onChange={() => setShowPath(!showPath)}
                  className="rounded text-blue-500 focus:ring-blue-500"
                />
                <span>Movement Path</span>
              </label>
            </div>
          </div>
          
          <div className="p-4 h-96 relative bg-gray-100">
            {/* Floor layout */}
            <svg width="100%" height="100%" viewBox="0 0 100 70" className="border rounded">
              {/* Room outlines */}
              {roomData.map(room => (
                <g key={room.id}>
                  {/* Heatmap overlay */}
                  {showHeatmap && heatMapData.find(data => data.roomId === room.id) && (
                    <rect
                      x={room.x}
                      y={room.y}
                      width={room.width}
                      height={room.height}
                      fill={getHeatmapColor(heatMapData.find(data => data.roomId === room.id)?.intensity || 0)}
                      fillOpacity={getOpacity(heatMapData.find(data => data.roomId === room.id)?.intensity || 0)}
                    />
                  )}
                  
                  {/* Room outline */}
                  <rect
                    x={room.x}
                    y={room.y}
                    width={room.width}
                    height={room.height}
                    stroke="#374151"
                    strokeWidth="0.5"
                    fill="none"
                  />
                  
                  {/* Room label */}
                  <text
                    x={room.x + room.width / 2}
                    y={room.y + room.height / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="2"
                    fill="#374151"
                  >
                    {room.name}
                  </text>
                </g>
              ))}
              
              {/* Movement path */}
              {showPath && (
                <g>
                  <polyline
                    points={pathData.map(point => `${point.x},${point.y}`).join(' ')}
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth="0.8"
                    strokeDasharray="0.8,0.8"
                  />
                  
                  {/* Path markers */}
                  {pathData.map((point, index) => (
                    <circle
                      key={index}
                      cx={point.x}
                      cy={point.y}
                      r="0.8"
                      fill={index === pathData.length - 1 ? '#ef4444' : '#3b82f6'}
                    />
                  ))}
                </g>
              )}
              
              {/* Current position marker */}
              <circle
                cx={currentLocation.x}
                cy={currentLocation.y}
                r="1.5"
                fill="#ef4444"
              >
                <animate 
                  attributeName="r" 
                  values="1.5;2;1.5" 
                  dur="2s" 
                  repeatCount="indefinite" 
                />
              </circle>
            </svg>
            
            {/* Legend */}
            <div className="absolute bottom-2 left-2 bg-white p-2 rounded shadow text-xs">
              <div className="flex items-center mb-1">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div>
                <span>High Activity</span>
              </div>
              <div className="flex items-center mb-1">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-1"></div>
                <span>Medium Activity</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
                <span>Low Activity</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right column - Activity log and alerts */}
        <div className="space-y-6">
          {/* Current location */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center mb-2">
              <MapPin className="text-blue-500 mr-2" />
              <h3 className="font-medium">Current Location</h3>
            </div>
            <p className="text-2xl font-bold mb-1">{currentRoom}</p>
            <div className="flex items-center text-sm text-gray-600">
              <Clock className="text-gray-400 mr-1" size={16} />
              <span>
                Last movement: {formatTime(lastMovementTime)}
              </span>
            </div>
          </div>
          
          {/* Recent activity log */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <div className="flex items-center">
                <BarChart2 className="text-gray-500 mr-2" />
                <h3 className="font-medium">Activity Log</h3>
              </div>
            </div>
            <div className="p-0">
              <ul className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                {activityData.map((activity, index) => (
                  <li key={index} className="flex p-3 hover:bg-gray-50">
                    <div className="flex-shrink-0 w-16 text-sm text-gray-500">
                      {activity.time}
                    </div>
                    <div className="ml-3 text-sm">
                      {activity.activity}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Abnormal patterns */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <div className="flex items-center">
                <AlertTriangle className="text-orange-500 mr-2" />
                <h3 className="font-medium">Abnormal Patterns</h3>
              </div>
            </div>
            <div className="p-2">
              {abnormalPatterns.length > 0 ? (
                abnormalPatterns.map((pattern, index) => (
                  <div key={index} className="p-3 mb-2 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                    <div className="flex justify-between">
                      <p className="font-medium">{pattern.concern}</p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        pattern.priority === 'HIGH' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {pattern.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Location: {pattern.location}
                    </p>
                    <p className="text-sm text-gray-600">
                      Detected: {formatTime(pattern.timestamp)}
                    </p>
                  </div>
                ))
              ) : (
                <div className="p-3 text-center text-gray-500">
                  No abnormal patterns detected
                </div>
              )}
            </div>
          </div>
          
          {/* Movement analysis */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center mb-3">
              <Zap className="text-purple-500 mr-2" />
              <h3 className="font-medium">Movement Analysis</h3>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between mb-1 text-sm">
                  <span>Gait Stability</span>
                  <span>85%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1 text-sm">
                  <span>Walk Speed</span>
                  <span>75%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1 text-sm">
                  <span>Fall Risk</span>
                  <span>20%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-red-500 h-2 rounded-full" style={{ width: '20%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResidentMonitor; 